import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { enqueueWarmTasks, fetchAllAccounts, type RelayConfig } from '../api/client';
import type { AccountRecord } from '../api/types';
import { Avatar } from '../components/Avatar';
import {
  IconActivity,
  IconAlert,
  IconBolt,
  IconChevronRight,
  IconClock,
  IconExternal,
  IconRefresh,
  IconSearch,
  IconUsers,
} from '../components/Icon';
import { SelectionBar } from '../components/SelectionBar';
import { StatCard } from '../components/StatCard';
import { StatusPill } from '../components/StatusPill';

interface Props {
  cfg: RelayConfig;
}

type Filter = 'all' | 'alive' | 'cold' | 'dead' | 'paused';

const HOUR = 1000 * 60 * 60;

export function Accounts({ cfg }: Props) {
  const [records, setRecords] = useState<AccountRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.url, cfg.token]);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const recs = await fetchAllAccounts(cfg);
      // Cold accounts surface first.
      recs.sort((a, b) => {
        const la = a.warming?.last_finished_at ?? '';
        const lb = b.warming?.last_finished_at ?? '';
        return la < lb ? -1 : la > lb ? 1 : 0;
      });
      setRecords(recs);
      setSelected((s) => {
        const valid = new Set(recs.map((r) => r.username.toLowerCase()));
        return new Set([...s].filter((u) => valid.has(u)));
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Stats for the KPI strip
  const stats = useMemo(() => {
    const all = records ?? [];
    const alive = all.filter((r) => isAlive(r)).length;
    const dead = all.filter((r) => isDead(r)).length;
    const cold = all.filter((r) => isAlive(r) && isCold(r)).length;
    const paused = all.filter((r) => r.warming?.last_status === 'paused').length;
    return { total: all.length, alive, dead, cold, paused };
  }, [records]);

  const filtered = useMemo(() => {
    const all = records ?? [];
    const q = query.trim().toLowerCase();
    return all.filter((r) => {
      if (filter === 'alive' && !isAlive(r)) return false;
      if (filter === 'dead' && !isDead(r)) return false;
      if (filter === 'paused' && r.warming?.last_status !== 'paused') return false;
      if (filter === 'cold') {
        if (!isAlive(r)) return false;
        if (!isCold(r)) return false;
      }
      if (q) {
        const hay =
          r.username.toLowerCase() +
          ' ' +
          (r.email ?? '').toLowerCase() +
          ' ' +
          (proxyHostport(r) ?? '').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [records, filter, query]);

  function toggle(username: string) {
    const u = username.toLowerCase();
    setSelected((s) => {
      const n = new Set(s);
      n.has(u) ? n.delete(u) : n.add(u);
      return n;
    });
  }

  function toggleAll() {
    if (filtered.length === 0) return;
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((r) => r.username.toLowerCase())));
    }
  }

  async function triggerSelected() {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const r = await enqueueWarmTasks(cfg, [...selected]);
      const parts = [`queued ${r.created} warm task${r.created === 1 ? '' : 's'}`];
      if (r.failed.length > 0) parts.push(`${r.failed.length} failed`);
      setToast(parts.join(', '));
      setSelected(new Set());
    } catch (e) {
      setToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Accounts</h1>
          <p className="mt-0.5 text-sm text-[var(--color-text-dim)]">
            All accounts stored in KV. Multi-select to queue a warming pass.
          </p>
        </div>
        <button onClick={refresh} className="btn-secondary" disabled={loading}>
          <IconRefresh
            width={14}
            height={14}
            className={loading ? 'animate-spin' : ''}
          />
          Refresh
        </button>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total" value={stats.total} icon={IconUsers} />
        <StatCard
          label="Alive"
          value={stats.alive}
          icon={IconActivity}
          tone="positive"
        />
        <StatCard
          label="Dead"
          value={stats.dead}
          icon={IconAlert}
          tone={stats.dead > 0 ? 'danger' : 'default'}
          hint="banned or suspended"
        />
        <StatCard
          label="Cold (24h+)"
          value={stats.cold}
          icon={IconClock}
          tone="warning"
          hint="alive, warmed > 24h ago"
        />
        <StatCard
          label="Paused"
          value={stats.paused}
          icon={IconAlert}
          tone={stats.paused > 0 ? 'danger' : 'default'}
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="relative flex-1 md:max-w-xs">
          <IconSearch
            width={14}
            height={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-faint)]"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search username, email, proxy…"
            className="input pl-8"
          />
        </div>
        <div className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-1">
          {(['all', 'alive', 'cold', 'dead', 'paused'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition ${
                filter === f
                  ? 'bg-[var(--color-surface-2)] text-zinc-100 shadow-sm shadow-black/40'
                  : 'text-[var(--color-text-dim)] hover:text-zinc-200'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {toast && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          {toast}
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-text-faint)]">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  checked={
                    filtered.length > 0 && selected.size === filtered.length
                  }
                  onChange={toggleAll}
                  className="h-3.5 w-3.5 cursor-pointer accent-emerald-500"
                />
              </th>
              <th className="px-3 py-3 font-medium">Account</th>
              <th className="px-3 py-3 font-medium">Health</th>
              <th className="px-3 py-3 font-medium">Karma</th>
              <th className="px-3 py-3 font-medium">Registered</th>
              <th className="px-3 py-3 font-medium">Last warmed</th>
              <th className="px-3 py-3 font-medium">Sessions</th>
              <th className="px-3 py-3 font-medium">Proxy</th>
              <th className="w-10 px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-[var(--color-text-dim)]">
                  Loading roster…
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-16 text-center">
                  <IconUsers
                    width={32}
                    height={32}
                    className="mx-auto text-[var(--color-text-faint)]"
                  />
                  <p className="mt-2 text-sm text-[var(--color-text-dim)]">
                    {query ? 'No accounts match the search.' : 'No accounts in this filter.'}
                  </p>
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <Row
                key={r.username}
                record={r}
                checked={selected.has(r.username.toLowerCase())}
                onToggle={() => toggle(r.username)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {records && (
        <div className="flex items-center justify-between text-xs text-[var(--color-text-faint)]">
          <span>
            Showing <strong className="text-zinc-300">{filtered.length}</strong> of{' '}
            {records.length}
          </span>
          {selected.size > 0 && <span>{selected.size} selected</span>}
        </div>
      )}

      <SelectionBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        onTrigger={triggerSelected}
        busy={busy}
      />
    </div>
  );
}

function Row({
  record,
  checked,
  onToggle,
}: {
  record: AccountRecord;
  checked: boolean;
  onToggle: () => void;
}) {
  const last = record.warming?.last_finished_at;
  const sessions = record.warming?.sessions_count ?? 0;
  return (
    <tr className="group transition hover:bg-[var(--color-surface-2)]/50">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="h-3.5 w-3.5 cursor-pointer accent-emerald-500"
          onClick={(e) => e.stopPropagation()}
        />
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          <Avatar name={record.username} />
          <div className="min-w-0">
            <a
              href={redditProfileUrl(record.username)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title={redditProfileUrl(record.username)}
              className="inline-flex items-center gap-1 truncate text-sm font-medium text-zinc-100 hover:text-emerald-300"
            >
              {record.username}
              <IconExternal
                width={11}
                height={11}
                className="text-[var(--color-text-faint)] transition group-hover:text-emerald-300"
              />
            </a>
            <div className="truncate text-xs text-[var(--color-text-faint)]">
              {record.email ?? '—'}
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <HealthPill record={record} />
      </td>
      <td className="px-3 py-3 tabular-nums text-xs text-zinc-400">
        {formatKarma(record)}
      </td>
      <td className="px-3 py-3">
        <span
          className="text-xs text-zinc-400"
          title={registeredTitle(record)}
        >
          {formatRegistered(record)}
        </span>
      </td>
      <td className="px-3 py-3">
        <span
          className="text-zinc-300"
          title={last ?? undefined}
        >
          {formatRelative(last)}
        </span>
      </td>
      <td className="px-3 py-3 tabular-nums text-zinc-400">
        {sessions === 0 ? (
          <span className="text-[var(--color-text-faint)]">—</span>
        ) : (
          sessions
        )}
        {(record.warming?.upvotes_total ?? 0) > 0 && (
          <span className="ml-2 inline-flex items-center gap-1 rounded bg-[var(--color-surface-2)] px-1.5 py-0.5 text-[10px] text-emerald-300 ring-1 ring-inset ring-emerald-500/20">
            <IconBolt width={10} height={10} />
            {record.warming?.upvotes_total}
          </span>
        )}
      </td>
      <td className="px-3 py-3">
        {record.proxy_mode === 'local_ip' ? (
          <span
            className="rounded bg-zinc-700/30 px-1.5 py-0.5 font-mono text-[11px] text-zinc-400 ring-1 ring-inset ring-zinc-600/40"
            title="Account runs through the host's local IP (no proxy)"
          >
            local_ip
          </span>
        ) : (
          <code
            className="rounded bg-[var(--color-surface-2)] px-1.5 py-0.5 font-mono text-[11px] text-zinc-400 ring-1 ring-inset ring-[var(--color-border)]"
            title={record.proxy_id ? `proxy_id: ${record.proxy_id}` : undefined}
          >
            {proxyHostport(record) ?? '—'}
          </code>
        )}
      </td>
      <td className="px-3 py-3 text-right">
        <Link
          to={`/accounts/${encodeURIComponent(record.username)}`}
          title="Open detail view"
          className="inline-flex items-center justify-center rounded-md p-1.5 text-[var(--color-text-faint)] transition hover:bg-[var(--color-surface-2)] hover:text-zinc-100"
        >
          <IconChevronRight />
        </Link>
      </td>
    </tr>
  );
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const dms = Date.now() - t;
  const m = Math.round(dms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function HealthPill({ record }: { record: AccountRecord }) {
  const health = record.health;
  if (health) {
    return <StatusPill status={health.status} />;
  }
  // Fall back to registration status when no probe has run yet — gives
  // the operator something useful for `pending` / `captcha` rows.
  const reg = record.status;
  if (reg && reg !== 'active') {
    return <StatusPill status={reg} />;
  }
  // Status==active but no health probe yet — neutral "unchecked".
  return (
    <span className="text-xs text-[var(--color-text-faint)]">unchecked</span>
  );
}

function isAlive(r: AccountRecord): boolean {
  // No health probe yet -> assume alive for legacy records (status=active);
  // post-register probe + sweeps will flip these to dead if appropriate.
  if (r.health?.status === 'alive') return true;
  if (isDead(r)) return false;
  return (r.status ?? 'active') === 'active';
}

function isDead(r: AccountRecord): boolean {
  const h = r.health?.status;
  return h === 'suspended' || h === 'banned' || h === 'not_found';
}

function isCold(r: AccountRecord): boolean {
  const last = r.warming?.last_finished_at;
  if (!last) return true;
  return Date.now() - new Date(last).getTime() > 24 * HOUR;
}

function formatKarma(r: AccountRecord): string {
  const lk = r.health?.link_karma ?? null;
  const ck = r.health?.comment_karma ?? null;
  if (lk === null && ck === null) return '—';
  return `${lk ?? 0} · ${ck ?? 0}`;
}

function redditProfileUrl(username: string): string {
  // Reddit's URL is case-insensitive but we keep whatever was registered for
  // visual consistency with the row.
  return `https://www.reddit.com/user/${encodeURIComponent(username)}`;
}

function formatRegistered(r: AccountRecord): string {
  const iso = bestRegisteredAt(r);
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const days = Math.floor((Date.now() - t) / (24 * HOUR));
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 60) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 18) return `${months} months ago`;
  const years = (days / 365).toFixed(1);
  return `${years} years ago`;
}

function registeredTitle(r: AccountRecord): string | undefined {
  return bestRegisteredAt(r) ?? undefined;
}

function bestRegisteredAt(r: AccountRecord): string | null {
  // `created_at` is what `register` writes (ISO-8601). Fall back to the
  // health-probe `created_utc` (Reddit's actual signup time as a Unix
  // timestamp) when only that is available.
  if (typeof r.created_at === 'string' && r.created_at) return r.created_at;
  const h = r.health as { created_utc?: number } | undefined;
  if (h && typeof h.created_utc === 'number' && h.created_utc > 0) {
    return new Date(h.created_utc * 1000).toISOString();
  }
  return null;
}

function proxyHostport(r: AccountRecord): string | null {
  if (r.proxy?.server) {
    try {
      const u = new URL(r.proxy.server);
      return u.host;
    } catch {
      return r.proxy.server;
    }
  }
  if (r.proxy_used) {
    return r.proxy_used.replace(/^https?:\/\//, '').split('/')[0];
  }
  return null;
}
